// src/components/Admin/Porting.tsx
import React, { useState, useEffect } from 'react';
import errorLogger from '../../services/errorLogger.service';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Grid,
  Tooltip,
  Tab,
  Tabs,
  Chip,
  CircularProgress,
  SelectChangeEvent,
  Badge,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  TrendingUp as EscalateIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// Create component logger
const logger = errorLogger.createComponentLogger('Porting');

interface Port {
  port_id: string;
  escalated: boolean;
  status: PortStatus;
  reseller: string;
  street_name: string;
  street_num: string;
  postal_code: string;
  street_suffix?: string;
  street_direction?: string;
  name: string;
  city: string;
  province: string;
  due_date: string;
  activation_date?: string;
  account_number?: string;
  phone_numbers: Record<string, PhoneNumberDetail>;
}

interface PhoneNumberDetail {
  account?: string;
  imei?: string;
  status: string;
  soa_complete: number;
  disconnect: number;
}

interface PortStatus {
  status: string;
  due_date?: string;
  cdate?: string;
  remarks?: string;
  errors?: PortError[];
}

interface PortError {
  error: string;
  phone_number?: string;
  remarks?: string;
  field?: string;
}

interface Batch {
  name: string;
  id: string;
  ports: string[];
  notes?: string;
  status: string;
  fs_account_num?: string;
  fs_brand?: string;
}

interface DraftRequest {
  id: number;
  custid: number;
  account_name: string;
  reseller: string;
  account_number?: string;
  street_num: string;
  street_name: string;
  street_suffix?: string;
  street_direction?: string;
  apartment?: string;
  city: string;
  province: string;
  postal_code: string;
  due_date: string;
  activation_date?: string;
  didGroup_id?: string;
  phone_numbers: any[];
  status: string;
  created_at: string;
  phone_count?: number;
}

const Porting: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { member } = useAuth();

  // State
  const [tabValue, setTabValue] = useState(0);
  const [ports, setPorts] = useState<Port[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [draftRequests, setDraftRequests] = useState<DraftRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' | 'warning' });
  const [selectedPort, setSelectedPort] = useState<Port | null>(null);
  const [portDetailDialogOpen, setPortDetailDialogOpen] = useState(false);

  // Modal state for Create Port Request and Check Numbers
  const [createPortDialogOpen, setCreatePortDialogOpen] = useState(false);
  const [checkNumbersDialogOpen, setCheckNumbersDialogOpen] = useState(false);
  const [updatePortDialogOpen, setUpdatePortDialogOpen] = useState(false);
  const [activationDateDialogOpen, setActivationDateDialogOpen] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState<number | null>(null);

  // Check Numbers state
  const [numberInput, setNumberInput] = useState('');
  const [numbersList, setNumbersList] = useState<string[]>([]);
  const [validationResults, setValidationResults] = useState<any>(null);

  // Search/filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Update port state
  const [updateForm, setUpdateForm] = useState({
    port_id: '',
    update_reason: '',
    account_name: '',
    reseller: '',
    account_number: '',
    street_num: '',
    street_name: '',
    street_suffix: '',
    street_direction: '',
    city: '',
    province: '',
    postal_code: '',
    due_date: '',
  });

  // Activation date state
  const [activationForm, setActivationForm] = useState({
    port_id: '',
    activation_date: ''
  });

  // Create Port Request form state
  const [portForm, setPortForm] = useState({
    custid: '',
    account_name: '',
    reseller: '',
    account_number: '',
    street_num: '',
    street_name: '',
    street_suffix: '',
    street_direction: '',
    apartment: '',
    city: '',
    province: 'ON',
    postal_code: '',
    due_date: '',
    activation_date: '',
    didGroup_id: '2037', // Default to On Net (IATS ACC2 ON NET)
    phone_numbers: [] as Array<{
      phone_number: string;
      imei: string;
      disconnect: boolean;
      ordering_notes: string;
    }>
  });

  useEffect(() => {
    loadData();
  }, [tabValue]);

  // Load draft count on component mount
  useEffect(() => {
    loadDraftRequests();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tabValue === 0) {
        await loadPorts();
      } else if (tabValue === 1) {
        await loadBatches();
      } else if (tabValue === 2) {
        await loadDraftRequests();
      } else if (tabValue === 3) {
        // Queue tab - no data to load yet
      }
    } catch (error) {
      logger.error('Error loading data', error);
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadPorts = async () => {
    try {
      // Load all port events
      const response = await api.get('/endpoints/porting/events');
      if (response.data.success && response.data.data?.ok === 1) {
        setPorts(response.data.data.events || []);
      } else {
        showSnackbar('Failed to load ports', 'error');
      }
    } catch (error) {
      logger.error('Error loading ports', error);
      throw error;
    }
  };

  const loadBatches = async () => {
    try {
      const response = await api.get('/endpoints/porting/batches');
      if (response.data.success && response.data.data?.ok === 1) {
        setBatches(response.data.data.batches || []);
      } else {
        showSnackbar('Failed to load batches', 'error');
      }
    } catch (error) {
      logger.error('Error loading batches', error);
      throw error;
    }
  };

  const loadDraftRequests = async () => {
    try {
      console.log('Loading draft requests...');
      const response = await api.get('/endpoints/porting/requests');
      console.log('Draft requests response:', response.data);
      if (response.data.success) {
        // Handle both array and object responses
        let dataArray = response.data.data;

        // If data is an object (not an array), try to extract the array
        if (dataArray && typeof dataArray === 'object' && !Array.isArray(dataArray)) {
          console.log('Data is an object, not an array. Keys:', Object.keys(dataArray));
          // Check if it has a 'data' property that is an array
          if (Array.isArray(dataArray.data)) {
            dataArray = dataArray.data;
          } else {
            // If it's a single object, wrap it in an array
            dataArray = [dataArray];
          }
        }

        // Ensure we have an array
        dataArray = Array.isArray(dataArray) ? dataArray : [];
        console.log('Data array:', dataArray);

        // Filter for only draft status requests
        const drafts = dataArray.filter((req: DraftRequest) => req.status === 'draft');
        console.log('Filtered drafts:', drafts);
        setDraftRequests(drafts);
      } else {
        // Silently fail - draft requests may not be available
        console.log('Draft requests failed:', response.data.message);
        logger.warning('Failed to load draft requests', response.data.message);
      }
    } catch (error) {
      // Silently fail - endpoint may not be available or tables may not exist
      console.log('Error loading draft requests:', error);
      logger.warning('Error loading draft requests', error);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewPortDetails = (port: Port) => {
    setSelectedPort(port);
    setPortDetailDialogOpen(true);
  };

  const handleCancelPort = async (portId: string) => {
    if (!window.confirm('Are you sure you want to cancel this port?')) return;

    try {
      setLoading(true);
      const response = await api.post(`/endpoints/porting/port/${portId}/cancel`);
      if (response.data.success) {
        showSnackbar('Port cancelled successfully', 'success');
        await loadPorts();
      } else {
        showSnackbar(response.data.message || 'Failed to cancel port', 'error');
      }
    } catch (error) {
      logger.error('Error cancelling port', error);
      showSnackbar('Error cancelling port', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEscalatePort = async (portId: string) => {
    if (!window.confirm('Escalate this port to Fibernetics support team?')) return;

    try {
      setLoading(true);
      const response = await api.post(`/endpoints/porting/port/${portId}/escalate`);
      if (response.data.success) {
        showSnackbar('Port escalated successfully', 'success');
        await loadPorts();
      } else {
        showSnackbar(response.data.message || 'Failed to escalate port', 'error');
      }
    } catch (error) {
      logger.error('Error escalating port', error);
      showSnackbar('Error escalating port', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNumber = () => {
    const trimmed = numberInput.trim();
    if (!trimmed) {
      showSnackbar('Please enter a phone number', 'warning');
      return;
    }

    // Check if already in list
    if (numbersList.includes(trimmed)) {
      showSnackbar('Number already in list', 'warning');
      return;
    }

    setNumbersList([...numbersList, trimmed]);
    setNumberInput('');
    showSnackbar('Number added to list', 'success');
  };

  const handleRemoveNumber = (number: string) => {
    setNumbersList(numbersList.filter(n => n !== number));
    showSnackbar('Number removed from list', 'info');
  };

  const handleClearList = () => {
    setNumbersList([]);
    setValidationResults(null);
    showSnackbar('List cleared', 'info');
  };

  const handleCheckNumbers = async () => {
    if (numbersList.length === 0) {
      showSnackbar('Please add phone numbers to check', 'warning');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/endpoints/porting/batch/validate', {
        phone_numbers: numbersList
      });

      if (response.data.success) {
        const results = response.data.data;

        // Sort results: errors first, then off-net, then on-switch, then on-net
        if (results.phone_numbers && Array.isArray(results.phone_numbers)) {
          results.phone_numbers.sort((a: any, b: any) => {
            // Errors first
            if (a.error && !b.error) return -1;
            if (!a.error && b.error) return 1;

            // Then off-net
            if (a.offnet && !b.offnet) return -1;
            if (!a.offnet && b.offnet) return 1;

            // Then on-switch
            if (a.onswitch && !b.onswitch) return -1;
            if (!a.onswitch && b.onswitch) return 1;

            // On-net last (these are good)
            return 0;
          });
        }

        setValidationResults(results);

        // Check if the Fibernetics API returned an error
        if (results && results.ok !== 1) {
          const errorMsg = results.message || results.error || 'Validation failed';
          showSnackbar(`Fibernetics API: ${errorMsg}`, 'warning');
        } else {
          showSnackbar('Numbers validated successfully', 'success');
        }
      } else {
        showSnackbar(response.data.message || 'Failed to validate numbers', 'error');
      }
    } catch (error) {
      logger.error('Error checking numbers', error);
      showSnackbar('Error checking numbers', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = () => {
    if (!validationResults || !validationResults.phone_numbers || validationResults.phone_numbers.length === 0) {
      showSnackbar('No validation results to export', 'warning');
      return;
    }

    // Create CSV content
    const headers = ['Phone Number', 'Status', 'On Network', 'Off Network', 'On Switch', 'Error'];
    const rows = validationResults.phone_numbers.map((info: any) => [
      info.tn,
      info.onnet ? 'On Network' : info.offnet ? 'Off Network' : info.onswitch ? 'On Switch' : 'Unknown',
      info.onnet ? 'Yes' : 'No',
      info.offnet ? 'Yes' : 'No',
      info.onswitch ? 'Yes' : 'No',
      info.error || '-'
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map((row: string[]) => row.map((cell: string) => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `porting-validation-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSnackbar('Validation results exported to CSV', 'success');
  };

  // Port form handlers
  const handlePortFormChange = (field: string, value: any) => {
    setPortForm({ ...portForm, [field]: value });
  };

  const handleAddPhoneNumber = () => {
    setPortForm({
      ...portForm,
      phone_numbers: [...portForm.phone_numbers, { phone_number: '', imei: '', disconnect: false, ordering_notes: '' }]
    });
  };

  const handleRemovePhoneNumber = (index: number) => {
    const updated = portForm.phone_numbers.filter((_, i) => i !== index);
    setPortForm({ ...portForm, phone_numbers: updated });
  };

  const handlePhoneNumberChange = (index: number, field: string, value: any) => {
    const updated = [...portForm.phone_numbers];
    updated[index] = { ...updated[index], [field]: value };
    setPortForm({ ...portForm, phone_numbers: updated });
  };

  const handleSaveDraft = async () => {
    try {
      setLoading(true);

      // Check if we're editing an existing draft or creating a new one
      let response;
      if (editingDraftId) {
        // Update existing draft
        response = await api.put(`/endpoints/porting/requests/${editingDraftId}`, portForm);
      } else {
        // Create new draft
        response = await api.post('/endpoints/porting/port/create', portForm);
      }

      if (response.data.success) {
        showSnackbar(editingDraftId ? 'Port request updated successfully' : 'Port request saved as draft', 'success');
        // Reload draft requests to update the badge count
        await loadDraftRequests();
        // Close the modal
        setCreatePortDialogOpen(false);
        // Reset editing mode
        setEditingDraftId(null);
        // Reset form
        setPortForm({
          custid: '',
          account_name: '',
          reseller: '',
          account_number: '',
          street_num: '',
          street_name: '',
          street_suffix: '',
          street_direction: '',
          apartment: '',
          city: '',
          province: 'ON',
          postal_code: '',
          due_date: '',
          activation_date: '',
          didGroup_id: '2037',
          phone_numbers: []
        });
      } else {
        showSnackbar(response.data.message || 'Failed to save draft', 'error');
      }
    } catch (error) {
      logger.error('Error saving draft', error);
      showSnackbar('Error saving draft', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePortFromValidation = () => {
    // Get successfully validated numbers (on-net only)
    const validNumbers = validationResults.phone_numbers
      .filter((info: any) => info.onnet === 1 && !info.error)
      .map((info: any) => ({
        phone_number: info.tn,
        imei: '',
        disconnect: false,
        ordering_notes: ''
      }));

    if (validNumbers.length === 0) {
      showSnackbar('No valid on-net numbers to port', 'warning');
      return;
    }

    // Populate form with validated numbers
    setPortForm({
      ...portForm,
      phone_numbers: validNumbers
    });

    // Close Check Numbers modal and open Create Port modal
    setCheckNumbersDialogOpen(false);
    setCreatePortDialogOpen(true);
    showSnackbar(`${validNumbers.length} validated numbers added to port request`, 'success');
  };

  const handleEditDraft = async (draft: DraftRequest) => {
    try {
      setLoading(true);
      // Fetch the full draft details from the API
      const response = await api.get(`/endpoints/porting/requests/${draft.id}`);
      console.log('Edit draft API response:', response.data);

      if (response.data.success) {
        // Handle nested data structure - API returns data.data.data
        let draftData = response.data.data;

        // If the data has another nested 'data' property, use that
        if (draftData && draftData.data) {
          draftData = draftData.data;
        }

        console.log('Draft data from API:', draftData);
        console.log('Phone numbers from API:', draftData.phone_numbers);

        // Populate the form with the draft data
        setPortForm({
          custid: draftData.custid?.toString() || '',
          account_name: draftData.account_name || '',
          reseller: draftData.reseller || '',
          account_number: draftData.account_number || '',
          street_num: draftData.street_num || '',
          street_name: draftData.street_name || '',
          street_suffix: draftData.street_suffix || '',
          street_direction: draftData.street_direction || '',
          apartment: draftData.apartment || '',
          city: draftData.city || '',
          province: draftData.province || 'ON',
          postal_code: draftData.postal_code || '',
          due_date: draftData.due_date || '',
          activation_date: draftData.activation_date || '',
          didGroup_id: draftData.didGroup_id?.toString() || '2037',
          phone_numbers: draftData.phone_numbers || []
        });

        console.log('Form populated with data');

        // Set editing mode
        setEditingDraftId(draft.id);

        // Open the create port modal (which will act as edit mode)
        setCreatePortDialogOpen(true);
        showSnackbar('Draft loaded for editing', 'info');
      } else {
        showSnackbar(response.data.message || 'Failed to load draft', 'error');
      }
    } catch (error) {
      logger.error('Error loading draft for editing', error);
      showSnackbar('Error loading draft', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDraft = async (draftId: number) => {
    if (!window.confirm('Are you sure you want to delete this draft port request?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.delete(`/endpoints/porting/requests/${draftId}`);

      if (response.data.success) {
        showSnackbar('Draft deleted successfully', 'success');
        // Reload the draft requests list
        await loadDraftRequests();
      } else {
        showSnackbar(response.data.message || 'Failed to delete draft', 'error');
      }
    } catch (error) {
      logger.error('Error deleting draft', error);
      showSnackbar('Error deleting draft', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPort = (port: Port) => {
    // Populate the update form with current port data
    setUpdateForm({
      port_id: port.port_id,
      update_reason: '',
      account_name: port.name,
      reseller: port.reseller,
      account_number: port.account_number || '',
      street_num: port.street_num,
      street_name: port.street_name,
      street_suffix: port.street_suffix || '',
      street_direction: port.street_direction || '',
      city: port.city,
      province: port.province,
      postal_code: port.postal_code,
      due_date: port.due_date,
    });
    setUpdatePortDialogOpen(true);
  };

  const handleUpdatePort = async () => {
    if (!updateForm.update_reason) {
      showSnackbar('Please select an update reason', 'warning');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(`/endpoints/porting/port/${updateForm.port_id}/update`, updateForm);

      if (response.data.success) {
        showSnackbar('Port updated successfully', 'success');
        setUpdatePortDialogOpen(false);
        await loadPorts();
      } else {
        showSnackbar(response.data.message || 'Failed to update port', 'error');
      }
    } catch (error) {
      logger.error('Error updating port', error);
      showSnackbar('Error updating port', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSetActivationDate = (port: Port) => {
    setActivationForm({
      port_id: port.port_id,
      activation_date: port.activation_date || ''
    });
    setActivationDateDialogOpen(true);
  };

  const handleSaveActivationDate = async () => {
    if (!activationForm.activation_date) {
      showSnackbar('Please select an activation date', 'warning');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(`/endpoints/porting/port/${activationForm.port_id}/activate`, {
        activation_date: activationForm.activation_date
      });

      if (response.data.success) {
        showSnackbar('Activation date set successfully', 'success');
        setActivationDateDialogOpen(false);
        await loadPorts();
      } else {
        showSnackbar(response.data.message || 'Failed to set activation date', 'error');
      }
    } catch (error) {
      logger.error('Error setting activation date', error);
      showSnackbar('Error setting activation date', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): 'default' | 'success' | 'error' | 'warning' | 'info' => {
    const statusCode = status.charAt(0);
    switch (statusCode) {
      case 'C': return 'success'; // Confirmed
      case 'R': return 'error'; // Rejected
      case 'E': return 'warning'; // Exceptions
      case 'W': return 'warning'; // Wait/Cancellation
      case 'N': case 'U': return 'error'; // No facilities/Unsuitable
      case 'A': return 'info'; // Accepted with changes
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    const statusCode = status.charAt(0);
    switch (statusCode) {
      case 'C': return <CheckCircleIcon fontSize="small" />;
      case 'R': return <CancelIcon fontSize="small" />;
      case 'E': case 'W': return <WarningIcon fontSize="small" />;
      default: return <InfoIcon fontSize="small" />;
    }
  };

  const filteredPorts = ports.filter(port => {
    const matchesSearch = searchQuery === '' ||
      Object.keys(port.phone_numbers).some(tn => tn.includes(searchQuery)) ||
      port.port_id.includes(searchQuery) ||
      port.name.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || port.status.status.startsWith(statusFilter);

    return matchesSearch && matchesStatus;
  });

  const renderPortsTab = () => (
    <Box>
      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Phone number, port ID, or name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="C">Confirmed</MenuItem>
                  <MenuItem value="R">Rejected</MenuItem>
                  <MenuItem value="E">Exceptions</MenuItem>
                  <MenuItem value="W">Waiting/Cancelled</MenuItem>
                  <MenuItem value="A">Accepted with Changes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={5} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadData}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => showSnackbar('Create batch functionality coming soon', 'info')}
              >
                Create Batch
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Ports Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Active Ports ({filteredPorts.length})
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Port ID</TableCell>
                    <TableCell>Phone Numbers</TableCell>
                    <TableCell>Account Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Reseller</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPorts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="textSecondary" py={3}>
                          No ports found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPorts.map((port) => (
                      <TableRow key={port.port_id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {port.port_id}
                          </Typography>
                          {port.escalated && (
                            <Chip label="Escalated" size="small" color="warning" sx={{ mt: 0.5 }} />
                          )}
                        </TableCell>
                        <TableCell>
                          {Object.keys(port.phone_numbers).map(tn => (
                            <Typography key={tn} variant="body2">
                              {tn}
                            </Typography>
                          ))}
                        </TableCell>
                        <TableCell>{port.name}</TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(port.status.status)}
                            label={port.status.status}
                            size="small"
                            color={getStatusColor(port.status.status)}
                          />
                          {port.status.errors && port.status.errors.length > 0 && (
                            <Typography variant="caption" display="block" color="error" mt={0.5}>
                              {port.status.errors.length} error(s)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{port.due_date || '-'}</TableCell>
                        <TableCell>{port.reseller}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewPortDetails(port)}>
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!port.escalated && (
                            <>
                              <Tooltip title="Edit Port">
                                <IconButton size="small" onClick={() => handleEditPort(port)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Set Activation Date">
                                <IconButton size="small" onClick={() => handleSetActivationDate(port)}>
                                  <ScheduleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Escalate">
                                <IconButton size="small" onClick={() => handleEscalatePort(port.port_id)}>
                                  <EscalateIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" color="error" onClick={() => handleCancelPort(port.port_id)}>
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderBatchesTab = () => (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Batches ({batches.length})
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Batch ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Ports</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="textSecondary" py={3}>
                          No batches found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    batches.map((batch) => (
                      <TableRow key={batch.id} hover>
                        <TableCell>{batch.id}</TableCell>
                        <TableCell>{batch.name}</TableCell>
                        <TableCell>
                          <Chip label={batch.status} size="small" />
                        </TableCell>
                        <TableCell>{batch.ports.length} ports</TableCell>
                        <TableCell>{batch.notes || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderDraftPortsTab = () => (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="h6">
              Draft Port Requests ({draftRequests.length})
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={() => setCheckNumbersDialogOpen(true)}
              >
                Check Numbers
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  // Reset form to blank when creating a new port
                  setPortForm({
                    custid: '',
                    account_name: '',
                    reseller: '',
                    account_number: '',
                    street_num: '',
                    street_name: '',
                    street_suffix: '',
                    street_direction: '',
                    apartment: '',
                    city: '',
                    province: 'ON',
                    postal_code: '',
                    due_date: '',
                    activation_date: '',
                    didGroup_id: '2037',
                    phone_numbers: []
                  });
                  setCreatePortDialogOpen(true);
                }}
              >
                Create Port Request
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadData}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Account Name</TableCell>
                    <TableCell>Reseller</TableCell>
                    <TableCell>Phone Numbers</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {draftRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="textSecondary" py={3}>
                          No pending drafts found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    draftRequests.map((draft) => (
                      <TableRow key={draft.id} hover>
                        <TableCell>{draft.id}</TableCell>
                        <TableCell>{draft.account_name}</TableCell>
                        <TableCell>{draft.reseller}</TableCell>
                        <TableCell>
                          {draft.phone_numbers && draft.phone_numbers.length > 0 ? (
                            <Box>
                              {draft.phone_numbers.slice(0, 3).map((phone: any, idx: number) => (
                                <Typography key={idx} variant="body2">
                                  {phone.phone_number}
                                </Typography>
                              ))}
                              {draft.phone_numbers.length > 3 && (
                                <Typography variant="caption" color="textSecondary">
                                  +{draft.phone_numbers.length - 3} more
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>{draft.due_date}</TableCell>
                        <TableCell>
                          {new Date(draft.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditDraft(draft)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              onClick={() => showSnackbar('Submit functionality coming soon', 'info')}
                            >
                              Submit Port
                            </Button>
                            <Tooltip title="Delete Draft">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteDraft(draft.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderCheckNumbersTab = () => (
    <Box>
      <Grid container spacing={3}>
        {/* Data Entry Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Add Phone Numbers
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Enter phone numbers one at a time to build your validation list
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone Number"
                  placeholder="e.g. 4165551234"
                  value={numberInput}
                  onChange={(e) => setNumberInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddNumber();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddNumber}
                  disabled={!numberInput.trim()}
                >
                  Add
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCheckNumbers}
                  disabled={numbersList.length === 0 || loading}
                  startIcon={<SearchIcon />}
                >
                  {loading ? 'Validating...' : 'Validate Numbers'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClearList}
                  disabled={numbersList.length === 0}
                >
                  Clear
                </Button>
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Numbers to Check ({numbersList.length})
              </Typography>

              {numbersList.length === 0 ? (
                <Alert severity="info">No numbers added yet</Alert>
              ) : (
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', p: 1 }}>
                  {numbersList.map((number, index) => (
                    <Chip
                      key={index}
                      label={number}
                      onDelete={() => handleRemoveNumber(number)}
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Validation Results Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Validation Results
                </Typography>
                {validationResults && validationResults.phone_numbers && validationResults.phone_numbers.length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExportToExcel}
                    disabled={loading}
                  >
                    Export to CSV
                  </Button>
                )}
              </Box>

              {!validationResults ? (
                <Alert severity="info">
                  Add phone numbers and click "Validate Numbers" to check portability
                </Alert>
              ) : validationResults.ok === 1 ? (
                <Box>
                  {validationResults.phone_numbers && validationResults.phone_numbers.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Phone Number</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Network</TableCell>
                            <TableCell>Error</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {validationResults.phone_numbers.map((info: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{info.tn}</TableCell>
                              <TableCell>
                                <Chip
                                  label={info.onnet ? 'On Network' : info.offnet ? 'Off Network' : info.onswitch ? 'On Switch' : 'Unknown'}
                                  size="small"
                                  color={info.onnet ? 'success' : info.error ? 'error' : 'default'}
                                />
                              </TableCell>
                              <TableCell>
                                {info.onnet === 1 && <Chip label="On-Net" size="small" color="success" />}
                                {info.offnet === 1 && <Chip label="Off-Net" size="small" />}
                                {info.onswitch === 1 && <Chip label="On-Switch" size="small" color="info" />}
                                {!info.onnet && !info.offnet && !info.onswitch && '-'}
                              </TableCell>
                              <TableCell>
                                {info.error ? (
                                  <Typography variant="caption" color="error">{info.error}</Typography>
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">No validation results</Alert>
                  )}
                </Box>
              ) : (
                <Alert severity="error">
                  {validationResults.message || 'Validation failed'}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderCreatePortTab = () => (
    <Box>
      <Grid container spacing={3}>
            {/* Customer Selection (Master Admin Only) */}
            {member?.memberType === 'master_admin' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Customer ID"
                  value={portForm.custid}
                  onChange={(e) => handlePortFormChange('custid', e.target.value)}
                  required
                  helperText="Enter the customer ID for this port request"
                />
              </Grid>
            )}

            {/* Account Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Account Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Holder Name"
                value={portForm.account_name}
                onChange={(e) => handlePortFormChange('account_name', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Current Service Provider</InputLabel>
                <Select
                  value={portForm.reseller}
                  label="Current Service Provider"
                  onChange={(e: SelectChangeEvent) => handlePortFormChange('reseller', e.target.value)}
                >
                  <MenuItem value="Bell">Bell</MenuItem>
                  <MenuItem value="Rogers">Rogers</MenuItem>
                  <MenuItem value="Telus">Telus</MenuItem>
                  <MenuItem value="Shaw">Shaw</MenuItem>
                  <MenuItem value="Videotron">Videotron</MenuItem>
                  <MenuItem value="Freedom Mobile">Freedom Mobile</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Number"
                value={portForm.account_number}
                onChange={(e) => handlePortFormChange('account_number', e.target.value)}
                helperText="Your account number with the losing carrier"
              />
            </Grid>

            {/* Service Address */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ mt: 2 }}>
                Service Address
              </Typography>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Street Number"
                value={portForm.street_num}
                onChange={(e) => handlePortFormChange('street_num', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Street Name"
                value={portForm.street_name}
                onChange={(e) => handlePortFormChange('street_name', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Suffix"
                value={portForm.street_suffix}
                onChange={(e) => handlePortFormChange('street_suffix', e.target.value)}
                placeholder="St, Ave, Rd"
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Direction"
                value={portForm.street_direction}
                onChange={(e) => handlePortFormChange('street_direction', e.target.value)}
                placeholder="N, S, E, W"
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Apt/Suite"
                value={portForm.apartment}
                onChange={(e) => handlePortFormChange('apartment', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="City"
                value={portForm.city}
                onChange={(e) => handlePortFormChange('city', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <FormControl fullWidth required>
                <InputLabel>Province</InputLabel>
                <Select
                  value={portForm.province}
                  label="Province"
                  onChange={(e: SelectChangeEvent) => handlePortFormChange('province', e.target.value)}
                >
                  <MenuItem value="ON">ON</MenuItem>
                  <MenuItem value="BC">BC</MenuItem>
                  <MenuItem value="AB">AB</MenuItem>
                  <MenuItem value="SK">SK</MenuItem>
                  <MenuItem value="MB">MB</MenuItem>
                  <MenuItem value="QC">QC</MenuItem>
                  <MenuItem value="NB">NB</MenuItem>
                  <MenuItem value="NS">NS</MenuItem>
                  <MenuItem value="PE">PE</MenuItem>
                  <MenuItem value="NL">NL</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Postal Code"
                value={portForm.postal_code}
                onChange={(e) => handlePortFormChange('postal_code', e.target.value)}
                required
              />
            </Grid>

            {/* Port Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ mt: 2 }}>
                Port Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                label="Requested Due Date"
                value={portForm.due_date}
                onChange={(e) => handlePortFormChange('due_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Activation Date/Time (Optional)"
                value={portForm.activation_date}
                onChange={(e) => handlePortFormChange('activation_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>DID Group</InputLabel>
                <Select
                  value={portForm.didGroup_id}
                  label="DID Group"
                  onChange={(e: SelectChangeEvent) => handlePortFormChange('didGroup_id', e.target.value)}
                >
                  <MenuItem value="2037">On Net</MenuItem>
                  <MenuItem value="2038">Off Net</MenuItem>
                  <MenuItem value="2039">Toll-Free</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Phone Numbers */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Phone Numbers to Port
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddPhoneNumber}
                >
                  Add Number
                </Button>
              </Box>

              {portForm.phone_numbers.length === 0 ? (
                <Alert severity="info">Click "Add Number" to add phone numbers to port</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Phone Number</TableCell>
                        <TableCell>IMEI (Wireless)</TableCell>
                        <TableCell>Disconnect</TableCell>
                        <TableCell>Circuit ID / Notes</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {portForm.phone_numbers.map((phone, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <TextField
                              size="small"
                              placeholder="4165551234"
                              value={phone.phone_number}
                              onChange={(e) => handlePhoneNumberChange(index, 'phone_number', e.target.value)}
                              required
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              placeholder="IMEI"
                              value={phone.imei}
                              onChange={(e) => handlePhoneNumberChange(index, 'imei', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small">
                              <Select
                                value={phone.disconnect ? '1' : '0'}
                                onChange={(e: SelectChangeEvent) => handlePhoneNumberChange(index, 'disconnect', e.target.value === '1')}
                              >
                                <MenuItem value="0">No</MenuItem>
                                <MenuItem value="1">Yes</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              placeholder="Circuit ID or notes"
                              value={phone.ordering_notes}
                              onChange={(e) => handlePhoneNumberChange(index, 'ordering_notes', e.target.value)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemovePhoneNumber(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Grid>

        {/* Form Actions */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setPortForm({
                  custid: '',
                  account_name: '',
                  reseller: '',
                  account_number: '',
                  street_num: '',
                  street_name: '',
                  street_suffix: '',
                  street_direction: '',
                  apartment: '',
                  city: '',
                  province: 'ON',
                  postal_code: '',
                  due_date: '',
                  activation_date: '',
                  didGroup_id: '2037',
                  phone_numbers: []
                });
              }}
            >
              Clear Form
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveDraft}
              disabled={loading || !portForm.account_name || !portForm.reseller || portForm.phone_numbers.length === 0}
            >
              {loading ? 'Saving...' : 'Save Draft'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  const renderQueueTab = () => (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Port Queue
          </Typography>
          <Alert severity="info">
            Queue monitoring functionality coming soon
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Number Porting Management
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Manage Local Number Portability (LNP) port requests and batches
      </Typography>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            label={
              <Badge badgeContent={ports.length} color="primary" showZero>
                Ports
              </Badge>
            }
          />
          <Tab label="Batches" />
          <Tab
            label={
              <Badge badgeContent={draftRequests.length} color="error" showZero>
                Draft Ports
              </Badge>
            }
          />
          <Tab label="Queue" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {tabValue === 0 && renderPortsTab()}
      {tabValue === 1 && renderBatchesTab()}
      {tabValue === 2 && renderDraftPortsTab()}
      {tabValue === 3 && renderQueueTab()}

      {/* Port Detail Dialog */}
      <Dialog
        open={portDetailDialogOpen}
        onClose={() => setPortDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Port Details - {selectedPort?.port_id}
        </DialogTitle>
        <DialogContent dividers>
          {selectedPort && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Account Name</Typography>
                  <Typography variant="body1">{selectedPort.name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Reseller</Typography>
                  <Typography variant="body1">{selectedPort.reseller}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip
                    icon={getStatusIcon(selectedPort.status.status)}
                    label={selectedPort.status.status}
                    color={getStatusColor(selectedPort.status.status)}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Due Date</Typography>
                  <Typography variant="body1">{selectedPort.due_date || 'Not set'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Address</Typography>
                  <Typography variant="body1">
                    {selectedPort.street_num} {selectedPort.street_name} {selectedPort.street_suffix}<br />
                    {selectedPort.city}, {selectedPort.province} {selectedPort.postal_code}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>Phone Numbers</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Phone Number</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>IMEI</TableCell>
                          <TableCell>Account</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(selectedPort.phone_numbers).map(([tn, details]) => (
                          <TableRow key={tn}>
                            <TableCell>{tn}</TableCell>
                            <TableCell>
                              <Chip
                                label={details.status}
                                size="small"
                                color={details.soa_complete === 1 ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>{details.imei || '-'}</TableCell>
                            <TableCell>{details.account || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                {selectedPort.status.errors && selectedPort.status.errors.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="error" gutterBottom>Errors</Typography>
                    {selectedPort.status.errors.map((error, index) => (
                      <Alert key={index} severity="error" sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight="medium">{error.error}</Typography>
                        {error.phone_number && (
                          <Typography variant="caption">Phone: {error.phone_number}</Typography>
                        )}
                        {error.field && (
                          <Typography variant="caption"> - Field: {error.field}</Typography>
                        )}
                        {error.remarks && (
                          <Typography variant="caption" display="block">{error.remarks}</Typography>
                        )}
                      </Alert>
                    ))}
                  </Grid>
                )}
                {selectedPort.status.remarks && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Remarks</Typography>
                    <Typography variant="body2">{selectedPort.status.remarks}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPortDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Port Request Modal */}
      <Dialog
        open={createPortDialogOpen}
        onClose={() => {
          setCreatePortDialogOpen(false);
          setEditingDraftId(null);
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Create Port Request
          <Typography variant="body2" color="textSecondary">
            Submit a new number port request
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {renderCreatePortTab()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreatePortDialogOpen(false);
            setEditingDraftId(null);
          }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Check Numbers Modal */}
      <Dialog
        open={checkNumbersDialogOpen}
        onClose={() => setCheckNumbersDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Check Numbers for Portability
        </DialogTitle>
        <DialogContent dividers>
          {renderCheckNumbersTab()}
        </DialogContent>
        <DialogActions>
          {validationResults && validationResults.ok === 1 && validationResults.phone_numbers &&
           validationResults.phone_numbers.some((info: any) => info.onnet === 1 && !info.error) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreatePortFromValidation}
            >
              Create Port with Validated Numbers
            </Button>
          )}
          <Button onClick={() => setCheckNumbersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Update Port Dialog */}
      <Dialog
        open={updatePortDialogOpen}
        onClose={() => setUpdatePortDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Update Port - {updateForm.port_id}
          <Typography variant="body2" color="textSecondary">
            Update port information in Fibernetics system
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                You must provide a reason for updating this port. All changes will be sent directly to the Fibernetics porting system.
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Update Reason</InputLabel>
                <Select
                  value={updateForm.update_reason}
                  label="Update Reason"
                  onChange={(e: SelectChangeEvent) => setUpdateForm({ ...updateForm, update_reason: e.target.value })}
                >
                  <MenuItem value="cancel">Cancel Port</MenuItem>
                  <MenuItem value="new-date">New Due Date</MenuItem>
                  <MenuItem value="correction">Correction to Information</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="medium" gutterBottom sx={{ mt: 2 }}>
                Account Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Holder Name"
                value={updateForm.account_name}
                onChange={(e) => setUpdateForm({ ...updateForm, account_name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Current Service Provider</InputLabel>
                <Select
                  value={updateForm.reseller}
                  label="Current Service Provider"
                  onChange={(e: SelectChangeEvent) => setUpdateForm({ ...updateForm, reseller: e.target.value })}
                >
                  <MenuItem value="Bell">Bell</MenuItem>
                  <MenuItem value="Rogers">Rogers</MenuItem>
                  <MenuItem value="Telus">Telus</MenuItem>
                  <MenuItem value="Shaw">Shaw</MenuItem>
                  <MenuItem value="Videotron">Videotron</MenuItem>
                  <MenuItem value="Freedom Mobile">Freedom Mobile</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account Number"
                value={updateForm.account_number}
                onChange={(e) => setUpdateForm({ ...updateForm, account_number: e.target.value })}
                helperText="Your account number with the losing carrier"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="medium" gutterBottom sx={{ mt: 2 }}>
                Service Address
              </Typography>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Street Number"
                value={updateForm.street_num}
                onChange={(e) => setUpdateForm({ ...updateForm, street_num: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Street Name"
                value={updateForm.street_name}
                onChange={(e) => setUpdateForm({ ...updateForm, street_name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Suffix"
                value={updateForm.street_suffix}
                onChange={(e) => setUpdateForm({ ...updateForm, street_suffix: e.target.value })}
                placeholder="St, Ave, Rd"
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Direction"
                value={updateForm.street_direction}
                onChange={(e) => setUpdateForm({ ...updateForm, street_direction: e.target.value })}
                placeholder="N, S, E, W"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={updateForm.city}
                onChange={(e) => setUpdateForm({ ...updateForm, city: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Province</InputLabel>
                <Select
                  value={updateForm.province}
                  label="Province"
                  onChange={(e: SelectChangeEvent) => setUpdateForm({ ...updateForm, province: e.target.value })}
                >
                  <MenuItem value="ON">ON</MenuItem>
                  <MenuItem value="BC">BC</MenuItem>
                  <MenuItem value="AB">AB</MenuItem>
                  <MenuItem value="SK">SK</MenuItem>
                  <MenuItem value="MB">MB</MenuItem>
                  <MenuItem value="QC">QC</MenuItem>
                  <MenuItem value="NB">NB</MenuItem>
                  <MenuItem value="NS">NS</MenuItem>
                  <MenuItem value="PE">PE</MenuItem>
                  <MenuItem value="NL">NL</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Postal Code"
                value={updateForm.postal_code}
                onChange={(e) => setUpdateForm({ ...updateForm, postal_code: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Requested Due Date"
                value={updateForm.due_date}
                onChange={(e) => setUpdateForm({ ...updateForm, due_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdatePortDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdatePort}
            variant="contained"
            disabled={loading || !updateForm.update_reason}
          >
            {loading ? 'Updating...' : 'Update Port'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set Activation Date Dialog */}
      <Dialog
        open={activationDateDialogOpen}
        onClose={() => setActivationDateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Set Activation Date - {activationForm.port_id}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Set the activation date and time for this port. This is when the number porting will take effect.
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Activation Date/Time"
                value={activationForm.activation_date}
                onChange={(e) => setActivationForm({ ...activationForm, activation_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivationDateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveActivationDate}
            variant="contained"
            disabled={loading || !activationForm.activation_date}
          >
            {loading ? 'Saving...' : 'Set Activation Date'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Porting;
